/**
 * Widget Compteur de Sessions Premium — Refonte lumineuse (Iter309)
 * ==================================================================
 *
 * Design ultra-premium light theme cohérent avec Iter304-308 :
 * - Fond blanc/ivoire (#FFFFFF / #FAFAF8)
 * - Bordures fines or (#E8E4D9 / #D4AF37)
 * - Accents : violet (>50%) · or (20-50%) · rose doux (<10%)
 * - Texte principal : bleu nuit #1C1147
 *
 * Logique métier inchangée (useSessionCredits, alertLevel, isB2B).
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useSessionCredits } from '../../hooks/useSessionCredits';
import { Sparkles, RefreshCw, ChevronRight, Award } from 'lucide-react';

/* ─── Tokens ─── */
const T = {
  ink: '#1C1147',
  inkSoft: '#5A5169',
  ivory: '#FAFAF8',
  border: '#E8E4D9',
  goldDeep: '#B7901F',
  gold: '#D4AF37',
  goldLight: '#F3D060',
  violet: '#7A4FCF',
  violetSoft: '#A78BFA',
  rose: '#C44B6F',
  amber: '#D49B2A',
};

export function SessionsWidget({ onRechargeClick, compact = false }) {
  const {
    current,
    bonus,
    totalAvailable,
    percentageRemaining,
    showQuota,
    alertLevel,
    isB2B,
    displaySubtitle,
    loading,
    refresh,
    isExhausted,
    needsRecharge,
  } = useSessionCredits();

  /* ─────────────── Cas legacy non-quota ─────────────── */
  if (!showQuota) {
    return (
      <div
        className="rounded-2xl p-5 flex items-center gap-3"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F9F4FF 100%)',
          border: `1px solid ${T.border}`,
          boxShadow: '0 1px 0 rgba(28,17,71,0.04), 0 14px 32px -22px rgba(28,17,71,0.20)',
        }}
        data-testid="sessions-premium-widget-legacy"
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft})`,
            boxShadow: '0 6px 18px -8px rgba(122,79,207,0.55)',
          }}
        >
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-semibold" style={{ color: T.ink }}>
            Accès Premium Entreprise
          </p>
          <p className="text-sm" style={{ color: T.inkSoft }}>
            Votre entreprise vous offre un accès complet.
          </p>
        </div>
      </div>
    );
  }

  /* ─────────────── Palette dynamique selon alerte ─────────────── */
  const getPalette = () => {
    if (isExhausted) {
      return {
        accent: T.rose,
        bar: `linear-gradient(90deg, ${T.rose}, #E07A98)`,
        chipBg: 'rgba(196,75,111,0.08)',
        chipBorder: 'rgba(196,75,111,0.30)',
        iconBg: `linear-gradient(135deg, ${T.rose}, #E07A98)`,
      };
    }
    if (alertLevel === 'soft_10') {
      return {
        accent: T.amber,
        bar: `linear-gradient(90deg, ${T.amber}, ${T.goldLight})`,
        chipBg: 'rgba(212,155,42,0.10)',
        chipBorder: 'rgba(212,155,42,0.35)',
        iconBg: `linear-gradient(135deg, ${T.goldDeep}, ${T.goldLight})`,
      };
    }
    if (alertLevel === 'soft_20' || percentageRemaining < 50) {
      return {
        accent: T.goldDeep,
        bar: `linear-gradient(90deg, ${T.goldDeep}, ${T.goldLight})`,
        chipBg: 'rgba(212,175,55,0.10)',
        chipBorder: 'rgba(212,175,55,0.35)',
        iconBg: `linear-gradient(135deg, ${T.goldDeep}, ${T.goldLight})`,
      };
    }
    return {
      accent: T.violet,
      bar: `linear-gradient(90deg, ${T.violet}, ${T.violetSoft})`,
      chipBg: 'rgba(122,79,207,0.08)',
      chipBorder: 'rgba(122,79,207,0.30)',
      iconBg: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft})`,
    };
  };
  const p = getPalette();

  /* ─────────────── B2B view ─────────────── */
  if (isB2B) {
    return (
      <div
        className="rounded-2xl p-6 relative overflow-hidden"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
          boxShadow:
            '0 1px 0 rgba(28,17,71,0.04), 0 18px 40px -24px rgba(28,17,71,0.22)',
        }}
        data-testid="sessions-premium-widget-b2b"
      >
        <span
          aria-hidden
          className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(122,79,207,0.10) 0%, transparent 70%)',
            filter: 'blur(20px)',
          }}
        />
        <div className="flex items-start justify-between mb-5 relative">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: p.iconBg,
                boxShadow: `0 8px 22px -10px ${p.accent}88`,
              }}
            >
              <Award className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-lg" style={{ color: T.ink, fontWeight: 600 }}>
                Accès Premium Entreprise
              </h3>
              <p className="text-sm" style={{ color: T.inkSoft }}>
                Offert par votre entreprise
              </p>
            </div>
          </div>
          <button
            onClick={refresh}
            className="p-2 rounded-lg transition-colors"
            style={{ color: T.inkSoft }}
            title="Actualiser"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span
              className="font-serif tabular-nums"
              style={{ color: T.ink, fontSize: '2.4rem', fontWeight: 700, lineHeight: 1 }}
              data-testid="sessions-current-b2b"
            >
              {current}
            </span>
            <span className="text-sm" style={{ color: T.inkSoft }}>
              sessions disponibles
            </span>
          </div>
        </div>

        <div className="mb-3">
          <div
            className="h-2 rounded-full overflow-hidden"
            style={{ background: 'rgba(28,17,71,0.06)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(percentageRemaining, 2)}%`,
                background: p.bar,
                transition: 'width 0.8s ease-out',
                boxShadow: `0 0 12px ${p.accent}55`,
              }}
            />
          </div>
        </div>

        {(alertLevel === 'soft_10' || alertLevel === 'exhausted') && (
          <div
            className="mt-3 p-3 rounded-xl"
            style={{
              background: p.chipBg,
              border: `1px solid ${p.chipBorder}`,
            }}
          >
            <p className="text-sm" style={{ color: T.ink }}>
              {alertLevel === 'exhausted'
                ? 'Sessions du mois utilisées. Contactez votre administrateur.'
                : 'Sessions bientôt épuisées. Contactez votre administrateur si besoin.'}
            </p>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────── Loading ─────────────── */
  if (loading) {
    return (
      <div
        className="rounded-2xl p-6 animate-pulse"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
        }}
      >
        <div className="h-4 rounded w-1/3 mb-3" style={{ background: 'rgba(28,17,71,0.06)' }} />
        <div className="h-3 rounded w-full mb-4" style={{ background: 'rgba(28,17,71,0.06)' }} />
        <div className="h-2 rounded w-2/3" style={{ background: 'rgba(28,17,71,0.06)' }} />
      </div>
    );
  }

  /* ─────────────── Compact ─────────────── */
  if (compact) {
    return (
      <div
        className="rounded-xl p-3 flex items-center justify-between"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
        }}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: p.accent }} />
          <span className="text-sm font-semibold" style={{ color: T.ink }}>
            {totalAvailable} session{totalAvailable > 1 ? 's' : ''}
          </span>
        </div>
        {needsRecharge && onRechargeClick && (
          <button
            onClick={onRechargeClick}
            className="text-xs font-semibold flex items-center gap-1"
            style={{ color: p.accent }}
          >
            Recharger <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  /* ─────────────── Version complète Premium ─────────────── */
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl p-6 relative overflow-hidden"
      style={{
        background: '#FFFFFF',
        border: `1px solid ${T.border}`,
        boxShadow:
          '0 1px 0 rgba(28,17,71,0.04), 0 18px 40px -24px rgba(28,17,71,0.22)',
      }}
      data-testid="sessions-premium-widget"
    >
      {/* halo top-right */}
      <span
        aria-hidden
        className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${p.accent}20 0%, transparent 70%)`,
          filter: 'blur(24px)',
        }}
      />

      {/* Header */}
      <div className="flex items-start justify-between mb-5 relative">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: p.iconBg,
              boxShadow: `0 8px 22px -10px ${p.accent}88, inset 0 1px 0 rgba(255,255,255,0.3)`,
            }}
          >
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3
              className="font-serif"
              style={{ color: T.ink, fontSize: '1.1rem', fontWeight: 600 }}
            >
              Vos Sessions Premium
            </h3>
            {displaySubtitle && (
              <p className="text-sm truncate" style={{ color: T.inkSoft }}>
                {displaySubtitle}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={refresh}
          className="p-2 rounded-lg transition-colors hover:bg-[#FFFCEC]"
          style={{ color: T.inkSoft }}
          title="Actualiser"
          data-testid="refresh-sessions-btn"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Compteur principal */}
      <div className="mb-4 relative">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="font-serif tabular-nums"
            style={{
              color: T.ink,
              fontSize: '2.6rem',
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
            data-testid="sessions-current"
          >
            {totalAvailable}
          </span>
          <span className="text-sm font-medium" style={{ color: T.inkSoft }}>
            crédits de sessions
          </span>
          {bonus > 0 && (
            <span
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full ml-1"
              style={{
                background: p.chipBg,
                color: p.accent,
                border: `1px solid ${p.chipBorder}`,
              }}
            >
              +{bonus} bonus
            </span>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      <div className="mb-5">
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: 'rgba(28,17,71,0.06)' }}
        >
          <motion.div
            className="h-full rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(percentageRemaining, 2)}%` }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
            style={{
              background: p.bar,
              boxShadow: `0 0 12px ${p.accent}55`,
            }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[11px]" style={{ color: T.inkSoft }}>
            {percentageRemaining}% restant
          </span>
          {alertLevel === 'soft_10' && (
            <span
              className="text-[11px] font-semibold"
              style={{ color: p.accent }}
            >
              Sessions bientôt épuisées
            </span>
          )}
        </div>
      </div>

      {/* CTA Recharge — bouton or premium */}
      {needsRecharge && onRechargeClick && (
        <button
          onClick={onRechargeClick}
          className="group w-full px-5 py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-all"
          style={{
            background: `linear-gradient(135deg, ${T.goldDeep} 0%, ${T.gold} 50%, ${T.goldLight} 100%)`,
            color: T.ink,
            border: '1px solid rgba(255,255,255,0.4)',
            boxShadow:
              '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow =
              '0 1px 0 rgba(255,255,255,0.4) inset, 0 18px 40px -12px rgba(212,175,55,0.7)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow =
              '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)';
          }}
          data-testid="recharge-sessions-btn"
        >
          <Sparkles className="w-4 h-4" />
          <span>Recharger des sessions</span>
          <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      )}
    </motion.div>
  );
}

export default SessionsWidget;
